import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { WIREFRAME_PATHS } from '../../routes'
import { Level } from '../../types'

// Where this classifier is applied. Applications exist only on profiles; this
// list groups them by organisation/team (plus directly-added profiles) so SD can
// act in bulk. appliedProfiles < totalProfiles is a coverage gap — profiles added
// under that org/team after the bulk-add, which nothing picks up automatically.
const ASSIGNMENTS: {
    id: string
    name: string
    level: Level
    context: string
    appliedProfiles: number
    totalProfiles: number
}[] = [
    {
        id: 'o-acme',
        name: 'Acme Corp',
        level: 'org',
        context: '3 teams',
        appliedProfiles: 84,
        totalProfiles: 88
    },
    {
        id: 'o-globex',
        name: 'Globex',
        level: 'org',
        context: '2 teams',
        appliedProfiles: 64,
        totalProfiles: 64
    },
    {
        id: 'o-stark',
        name: 'Stark Industries',
        level: 'org',
        context: '5 teams',
        appliedProfiles: 210,
        totalProfiles: 210
    },
    {
        id: 't-soylent-social',
        name: 'Soylent — Social',
        level: 'team',
        context: 'Soylent Media',
        appliedProfiles: 40,
        totalProfiles: 40
    },
    {
        id: 'p-acme-uk-yt',
        name: '@AcmeUK · YouTube',
        level: 'profile',
        context: 'Acme — Social',
        appliedProfiles: 1,
        totalProfiles: 1
    }
]

const LEVEL_PATH: Record<Level, string> = {
    org: WIREFRAME_PATHS.orgConfig,
    team: WIREFRAME_PATHS.teamConfig,
    profile: WIREFRAME_PATHS.profileConfig
}

/**
 * The detailed list behind the Live status profile count. A full-width section
 * keeps the editor as the focus while letting Service Delivery see exactly which
 * profiles are affected.
 */
export default function AppliedToTable({
    profileCount,
    onAdd
}: {
    profileCount: number
    onAdd: () => void
}) {
    // Search across the assignments — matches the entity name or its context
    // (so typing a team or org name finds the rows scoped to it).
    const [usageQuery, setUsageQuery] = useState('')
    const filteredAssignments = useMemo(() => {
        const q = usageQuery.trim().toLowerCase()
        if (!q) return ASSIGNMENTS
        return ASSIGNMENTS.filter(
            (a) =>
                a.name.toLowerCase().includes(q) ||
                a.context.toLowerCase().includes(q) ||
                a.level.toLowerCase().includes(q)
        )
    }, [usageQuery])

    return (
        <div className="bg-white border border-gray-200 rounded-lg">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-4 py-3">
                <span className="flex items-center text-[13px] font-semibold text-gray-900">
                    Applied to
                    <span className="ml-2 text-xs font-normal text-gray-600">
                        {filteredAssignments.length} of {ASSIGNMENTS.length} · covers{' '}
                        {profileCount} profiles
                    </span>
                </span>
                <div className="flex items-center gap-2">
                    <input
                        type="search"
                        value={usageQuery}
                        onChange={(e) => setUsageQuery(e.target.value)}
                        placeholder="Search profile, team or org…"
                        className="w-56 rounded-lg border border-gray-300 px-3 py-1.5 text-xs text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-arwen-300"
                    />
                    <button
                        type="button"
                        onClick={onAdd}
                        className="rounded-lg bg-arwen-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-arwen-700"
                    >
                        + Add
                    </button>
                </div>
            </div>
            <table className="w-full border-collapse text-sm">
                <thead>
                    <tr className="border-b border-gray-100 text-left">
                        {['Applied to', 'Coverage'].map((h) => (
                            <th
                                key={h}
                                scope="col"
                                className="px-4 py-2 text-[10.5px] font-semibold uppercase tracking-wide text-gray-700"
                            >
                                {h}
                            </th>
                        ))}
                        <th scope="col" className="px-4 py-2">
                            <span className="sr-only">Actions</span>
                        </th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                    {filteredAssignments.map((a) => {
                        const missing = a.totalProfiles - a.appliedProfiles
                        return (
                            <tr key={a.id}>
                                <td className="px-4 py-2.5">
                                    <Link
                                        to={LEVEL_PATH[a.level]}
                                        className="font-semibold text-arwen-700 hover:underline"
                                    >
                                        {a.name}
                                    </Link>
                                    <div className="text-[11.5px] text-gray-800">{a.context}</div>
                                </td>
                                <td className="px-4 py-2.5 text-gray-800">
                                    {a.level === 'profile' ? (
                                        '1 profile'
                                    ) : (
                                        <>
                                            <span
                                                className={`font-mono tabular-nums ${
                                                    missing > 0 ? 'font-semibold text-suspect' : ''
                                                }`}
                                            >
                                                {a.appliedProfiles}/{a.totalProfiles}
                                            </span>{' '}
                                            profiles
                                            {missing > 0 && (
                                                <button
                                                    type="button"
                                                    className="mt-0.5 block text-xs font-semibold text-arwen-700 hover:underline"
                                                >
                                                    Apply to remaining {missing}
                                                </button>
                                            )}
                                        </>
                                    )}
                                </td>
                                <td className="px-4 py-2.5 text-right">
                                    <button
                                        type="button"
                                        className="whitespace-nowrap text-xs font-semibold text-severe hover:underline"
                                        aria-label={`Remove this classifier from ${a.name}'s profiles`}
                                    >
                                        {a.level === 'profile'
                                            ? 'Remove'
                                            : 'Remove from these profiles'}
                                    </button>
                                </td>
                            </tr>
                        )
                    })}
                    {filteredAssignments.length === 0 && (
                        <tr>
                            <td colSpan={3} className="px-4 py-6 text-center text-xs text-gray-800">
                                Nothing matches &ldquo;{usageQuery}&rdquo;.
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
            <div className="border-t border-gray-100 px-4 py-2.5 text-[11.5px] text-gray-800">
                Removing an organisation or team here removes the classifier from all of its
                profiles at once.
            </div>
        </div>
    )
}
